import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  Clock,
  Repeat,
  Video,
  User,
  Database,
  Edit,
  Save,
} from "lucide-react";
import StateManagedSelect from "react-select";
import {
  useFetchAppointmentTypesMutation,
  useUpdateAppointmentTypeMutation,
} from "../../../../features/appointment/appointmentApiSlice";
import { selectUser } from "../../../../features/auth/authSlice";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { debounce } from "lodash";

const VisitTypesTable = () => {
  const [visitTypes, setVisitTypes] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [format, setFormat] = useState({
    label: "All",
    value: "all",
  });
  const [type, setType] = useState({
    label: "All",
    value: "all",
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedVisitType, setSelectedVisitType] = useState(null);
  const [getAppointmentTypes] = useFetchAppointmentTypesMutation();
  const [evw, setEvw] = useState(0);
  const [rotation, setRotation] = useState(null);
  const [isTelehealth, setIsTelehealth] = useState("telehealth");
  const [updateAppointmentType] = useUpdateAppointmentTypeMutation();

  const user = useSelector(selectUser);

  const calculateTotalPages = (totalResults, take) => {
    return Math.ceil(totalResults / take);
  };

  const fetchAppointmentTypes = async () => {
    try {
      const { data } = await getAppointmentTypes({
        organisationId: `${user?.organisation.id}`,
        search,
        take: "5",
        rotation:
          type.value === "all"
            ? "all"
            : type.value === "recurring"
            ? true
            : false,
        format: format.value,
        page: `${page}`,
        dispatch: "all",
        screen: "all",
      });

      setTotalResults(data.data.count);

      setTotalPages(calculateTotalPages(data.data.count, 5));

      const formattedData = data.data.appointmentTypes.map((visit) => ({
        ...visit,
        isEditing: false,
      }));

      setVisitTypes(formattedData);
    } catch (error) {
      console.error(error.data.message);
    }
  };

  const toggleEdit = (id) => {
    setVisitTypes(
      visitTypes.map((visit) =>
        visit.id === id ? { ...visit, isEditing: !visit.isEditing } : visit
      )
    );
  };

  useEffect(() => {
    fetchAppointmentTypes();
  }, [page, search, format, type]);

  const debouncedSearch = useCallback(
    debounce((value) => setSearch(value), 500),
    []
  );

  const renderPaginationButtons = (currentPage, totalPages) => {
    const maxVisiblePages = 5; // Maximum number of page buttons to show
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      // If total pages is less than max visible, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        endPage = 4;
      }
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis if needed
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handleSave = async (visit) => {
    try {
      await updateAppointmentType({
        appointmentTypeId: visit.id,
        // is_telehealth: isTelehealth === "telehealth" ? true : false,
        early_visit_window: +evw,
        rotation: +rotation === 0 ? null : +rotation,
      }).unwrap();

      setVisitTypes(
        visitTypes.map((v) =>
          v.id === visit.id
            ? {
                ...v,
                isEditing: false,
                earlyVisitWindow: +evw,
                rotation: +rotation === 0 ? null : +rotation,
                // isTelehealth: isTelehealth === "telehealth" ? true : false,
              }
            : v
        )
      );

      setEvw(0);
      setRotation(null);
      // setIsTelehealth("telehealth");
    } catch (error) {
      console.error("Error updating visit type:", error);
    }
  };

  return (
    <div className="flex flex-col p-6 min-h-screen bg-gray-50">
      {/* Header with logo and navigation - mimicking existing layout */}
      <div className="flex justify-between items-center mb-8">
        <div className="text-2xl font-semibold text-gray-800">Visit Types</div>
        <div className="text-sm text-gray-500">{totalResults} Visit Types</div>
      </div>

      {/* Search and filter row */}
      <div className="flex flex-wrap gap-4 mb-6">
        {user?.permissions?.includes('visit_types.search') && <div className="relative flex-grow max-w-md">
          <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            onChange={(e) => debouncedSearch(e.target.value)}
            className="w-full h-[38px] pl-10 pr-4 border border-1 border-slate-800 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search by Name..."
          />
        </div>}

        {user?.permissions?.includes('visit_types.filter_type') && <div className="relative flex-grow max-w-xs">
          <StateManagedSelect
            placeholder="Filter by Type..."
            options={[
              { label: "Recurring", value: "recurring" },
              { label: "As Needed", value: "asneeded" },
              { label: "All", value: "all" },
            ]}
            value={type}
            onChange={(e) => {
              setType(e);
            }}
            defaultValue={type}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
          />
        </div>}

        {user?.permissions?.includes('visit_types.filter_format') && <div className="relative flex-grow max-w-xs">
          <StateManagedSelect
            placeholder="Filter by Format..."
            options={[
              { label: "In-Person", value: "inperson" },
              { label: "Telehealth", value: "telehealth" },
              { label: "All", value: "all" },
            ]}
            value={format}
            onChange={(e) => {
              setFormat(e);
            }}
            defaultValue={format}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
          />
        </div>}
      </div>

      {/* Table */}
      <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="text-white bg-blue-600">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                Duration (min)
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                Frequency (days)
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                Early Visit Window (days)
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                EHR ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left"
              >
                Format
              </th>
            {user?.permissions?.includes('visit_types.edit_visit_type') && <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-right"
              >
                Edit
              </th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visitTypes.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  No visit types found.
                </td>
              </tr>
            ) : (
              visitTypes.map((visit) => (
                <tr key={visit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {visit.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Clock className="mr-2 w-4 h-4 text-gray-400" />
                      <span>{visit.duration}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {visit.rotation !== null ? (
                        <Repeat className="mr-2 w-4 h-4 text-purple-500" />
                      ) : (
                        <Clock className="mr-2 w-4 h-4 text-orange-500" />
                      )}
                      <span>
                        {visit.rotation === null ? "As Needed" : "Recurring"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {visit.isEditing ? (
                      visit.rotation !== null ? (
                        <div className="flex items-center">
                          <input
                            type="number"
                            className="px-2 py-1 w-16 text-sm rounded-md border border-gray-300 form-input"
                            onChange={(e) => setRotation(e.target.value)}
                            value={rotation}
                            min="0"
                          />
                          <span className="ml-2 text-sm text-gray-500">
                            days
                          </span>
                        </div>
                      ) : (
                        <div className="text-sm italic text-gray-400">N/A</div>
                      )
                    ) : (
                      <div>{visit.rotation ? visit.rotation : "N/A"}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {visit.isEditing ? (
                      <div className="flex items-center">
                        <input
                          type="number"
                          className="px-2 py-1 w-16 text-sm rounded-md border border-gray-300 form-input"
                          defaultValue={visit.earlyVisitWindow}
                          min="0"
                          onChange={(e) => setEvw(e.target.value)}
                        />
                        <span className="ml-2 text-sm text-gray-500">days</span>
                      </div>
                    ) : (
                      <div>
                        {visit.earlyVisitWindow > 0
                          ? visit.earlyVisitWindow
                          : "N/A"}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Database className="mr-2 w-4 h-4 text-gray-400" />
                      <span className="font-mono text-xs">{visit.ehrId}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {!visit.isTelehealth ? (
                        <div className="flex items-center">
                          <User className="mr-1 w-4 h-4 text-green-500" />
                          <span className="text-green-500">In-Person</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <Video className="mr-1 w-4 h-4 text-blue-500" />
                          <span className="text-blue-500">Telehealth</span>
                        </div>
                      )}
                    </div>
                  </td>
                  {user?.permissions?.includes('visit_types.edit_visit_type') && <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                    {visit.isEditing ? (
                      <button
                        onClick={() => {
                          toggleEdit(visit.id);
                          handleSave(visit);
                        }}
                        className="p-2 text-green-600 bg-green-50 rounded-md hover:text-green-900"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          toggleEdit(visit.id);
                          setEvw(visit.earlyVisitWindow);
                          setRotation(visit.rotation);
                        }}
                        className="p-2 text-blue-600 bg-blue-50 rounded-md hover:text-blue-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </td>}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {visitTypes.length !== 0 && (
          <div className="flex justify-between items-center px-6 py-3 border-t border-gray-200">
            <div className="flex flex-1 justify-between sm:hidden">
              <button className="inline-flex relative items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50">
                Previous
              </button>
              <button className="inline-flex relative items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">{(page - 1) * 5 + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min(totalResults, (page - 1) * 5 + 5)}
                  </span>{" "}
                  of <span className="font-medium">{totalResults}</span> results
                </p>
              </div>
              <div>
                <nav
                  className="inline-flex relative z-0 -space-x-px rounded-md shadow-sm"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => {
                      if (page > 1) {
                        setPage(page - 1);
                      }
                    }}
                    className="inline-flex relative items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white rounded-l-md border border-gray-300 hover:bg-gray-50"
                  >
                    <span className="sr-only">Previous</span>
                    <svg
                      className="w-5 h-5"
                      xmlns="<http://www.w3.org/2000/svg>"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {renderPaginationButtons(page, totalPages).map(
                    (pageNum, index) => (
                      <button
                        key={index}
                        className={classNames(
                          "relative inline-flex items-center px-4 py-2 text-sm font-medium border border-gray-300",
                          pageNum === "..."
                            ? "bg-white text-gray-700 cursor-default"
                            : page === pageNum
                            ? "bg-blue-500 text-white hover:bg-blue-400 ease-in-out duration-150"
                            : "bg-white text-gray-700 hover:bg-gray-900 ease-in-out duration-150"
                        )}
                        onClick={() => pageNum !== "..." && setPage(pageNum)}
                        disabled={pageNum === "..."}
                      >
                        {pageNum}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => {
                      if (page < totalPages) {
                        setPage(page + 1);
                      }
                    }}
                    className="inline-flex relative items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white rounded-r-md border border-gray-300 hover:bg-gray-50"
                  >
                    <span className="sr-only">Next</span>
                    <svg
                      className="w-5 h-5"
                      xmlns="<http://www.w3.org/2000/svg>"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitTypesTable;
